import { Box, Typography, useTheme } from "@mui/material";

/**
 * WHB brand logo — the hat shape stays the same, only the stroke colour flips
 * with theme so the outline is always visible against the background.
 *
 *   Dark theme  →  white strokes on transparent  (whb-hat-white, whb-logo-full-light)
 *   Light theme →  black strokes on transparent  (whb-hat-black, whb-logo-full)
 *
 * Both are real assets from the WHB CDN (the light versions are channel-negated
 * copies generated via ImageMagick). The hat interior is transparent — so on
 * dark it appears as white-on-navy, on light as outlined-white-on-cream.
 *
 * Variants:
 *   <WhbLogo />             — just the hat
 *   <WhbLogo variant="full"> — horizontal hat + wordmark
 *   withWordmark             — mark + adjacent text block (sidebar use)
 */

interface Props {
  size?: number;
  withWordmark?: boolean;
  variant?: "mark" | "full";
}

export function WhbLogo({ size = 40, withWordmark = false, variant = "mark" }: Props) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";

  const markSrc = dark ? "/icons/whb-hat-white.png"        : "/icons/whb-hat-black.png";
  const fullSrc = dark ? "/icons/whb-logo-full-light.png" : "/icons/whb-logo-full.png";

  if (variant === "full") {
    return (
      <Box
        component="img"
        src={fullSrc}
        alt="White Hat Bots"
        sx={{ height: size, width: "auto", display: "block" }}
      />
    );
  }

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5 }}>
      <Box
        component="img"
        src={markSrc}
        alt="White Hat Bots"
        sx={{ height: size, width: "auto", display: "block" }}
      />
      {withWordmark && (
        <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
          <Typography
            sx={{
              fontFamily: '"Roboto Flex Variable", sans-serif',
              fontWeight: 800,
              fontSize: "1.0625rem",
              letterSpacing: "-0.01em",
              color: "text.primary"
            }}
          >
            White Hat Bots
          </Typography>
          <Typography
            sx={{
              mt: 0.25,
              fontSize: "0.625rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fontWeight: 700,
              color: "text.secondary"
            }}
          >
            Trading Platform
          </Typography>
        </Box>
      )}
    </Box>
  );
}
